(function () {
  const SELECTORS = {
    container: '[data-video-media]',
    nativeVideo: '[data-video-element]',
    poster: '[data-video-poster]',
    indicator: '[data-video-indicator]',
    externalVideo: '[data-external-video]',
    iframeHolder: '[data-video-iframe-holder]'
  };

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsIntersectionObserver = 'IntersectionObserver' in window;

  function hidePoster(container) {
    const poster = container.querySelector(SELECTORS.poster);
    if (poster) poster.classList.add('is-hidden');
  }

  function showPoster(container) {
    const poster = container.querySelector(SELECTORS.poster);
    if (poster) poster.classList.remove('is-hidden');
  }

  function hideIndicator(container) {
    const indicator = container.querySelector(SELECTORS.indicator);
    if (indicator) indicator.classList.add('is-hidden');
  }

  function showIndicator(container) {
    const indicator = container.querySelector(SELECTORS.indicator);
    if (indicator) indicator.classList.remove('is-hidden');
  }

  function loadSources(video) {
    if (!video || video.dataset.loaded === 'true') return;

    const sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) {
      video.dataset.loaded = 'true';
      return;
    }

    sources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.load();
    video.dataset.loaded = 'true';
  }

  function playVideo(video, container) {
    if (!video) return;

    const result = video.play();
    if (result && typeof result.then === 'function') {
      result
        .then(() => {
          hidePoster(container);
          hideIndicator(container);
        })
        .catch(() => {
          showIndicator(container);
        });
    } else {
      hidePoster(container);
      hideIndicator(container);
    }
  }

  function pauseVideo(video, container) {
    if (!video) return;
    video.pause();
    showIndicator(container);
  }

  function buildExternalSrc(el) {
    const provider = el.dataset.provider;
    const id = el.dataset.videoId;
    const autoplay = el.dataset.autoplay === 'true' && !prefersReducedMotion.matches ? 1 : 0;
    const muted = el.dataset.muted === 'true' ? 1 : 0;
    const loop = el.dataset.loop === 'true' ? 1 : 0;
    const controls = el.dataset.controls === 'true' ? 1 : 0;

    if (provider === 'vimeo') {
      return 'https://player.vimeo.com/video/' + id +
        '?autoplay=' + autoplay +
        '&muted=' + muted +
        '&loop=' + loop +
        '&background=' + (controls ? 0 : 1) +
        '&controls=' + controls +
        '&title=0&byline=0&portrait=0';
    }

    if (provider === 'youtube') {
      return 'https://www.youtube.com/embed/' + id +
        '?autoplay=' + autoplay +
        '&mute=' + muted +
        '&loop=' + loop +
        '&playlist=' + id +
        '&controls=' + controls +
        '&modestbranding=1&rel=0&playsinline=1';
    }

    return '';
  }

  function injectExternalVideo(el, container) {
    if (!el || el.dataset.loaded === 'true') return;

    const holder = el.querySelector(SELECTORS.iframeHolder);
    if (!holder) return;

    const src = buildExternalSrc(el);
    if (!src) return;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.className = 'c-video-media__iframe';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', 'Embedded video');

    holder.appendChild(iframe);
    el.dataset.loaded = 'true';

    iframe.addEventListener('load', function () {
      hidePoster(container);
    }, { once: true });
  }

  function initNativeVideo(container) {
    if (!container || container.dataset.videoReady === 'true') return;

    const video = container.querySelector(SELECTORS.nativeVideo);
    if (!video) return;

    const lazyLoad = video.dataset.lazyLoad === 'true';
    const playWhenVisible = container.dataset.playWhenVisible === 'true';

    video.muted = video.dataset.muted === 'true';
    video.loop = video.dataset.loop === 'true';

    if (prefersReducedMotion.matches) {
      video.removeAttribute('autoplay');
    }

    if (!lazyLoad) {
      video.dataset.loaded = 'true';
    }

    if (video.autoplay && !prefersReducedMotion.matches) {
      hideIndicator(container);
    } else {
      showIndicator(container);
    }

    video.addEventListener('loadeddata', function () {
      hidePoster(container);
    });

    video.addEventListener('play', function () {
      hidePoster(container);
      hideIndicator(container);
    });

    video.addEventListener('pause', function () {
      showIndicator(container);
    });

    video.addEventListener('ended', function () {
      if (!video.loop) {
        showIndicator(container);
      }
    });

    video.addEventListener('error', function () {
      showPoster(container);
      showIndicator(container);
    });

    video.addEventListener('click', function () {
      if (lazyLoad && video.dataset.loaded !== 'true') {
        loadSources(video);
      }

      if (video.paused) {
        playVideo(video, container);
      } else {
        pauseVideo(video, container);
      }
    });

    if (!supportsIntersectionObserver) {
      if (lazyLoad) {
        loadSources(video);
      }

      if (video.autoplay && !prefersReducedMotion.matches) {
        playVideo(video, container);
      }

      container.dataset.videoReady = 'true';
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (lazyLoad && video.dataset.loaded !== 'true') {
              loadSources(video);
            }

            if (playWhenVisible && video.autoplay && !prefersReducedMotion.matches) {
              playVideo(video, container);
            }
          } else {
            if (playWhenVisible && !video.paused) {
              pauseVideo(video, container);
            }
          }
        });
      },
      {
        rootMargin: '200px 0px'
      }
    );

    observer.observe(container);
    container.dataset.videoReady = 'true';
  }

  function initExternalVideo(container) {
    if (!container || container.dataset.externalReady === 'true') return;

    const external = container.querySelector(SELECTORS.externalVideo);
    if (!external) return;

    const lazyLoad = external.dataset.lazyLoad === 'true';

    if (!supportsIntersectionObserver) {
      injectExternalVideo(external, container);
      container.dataset.externalReady = 'true';
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (lazyLoad) {
              injectExternalVideo(external, container);
            } else if (external.dataset.loaded !== 'true') {
              injectExternalVideo(external, container);
            }
          }
        });
      },
      {
        rootMargin: '200px 0px'
      }
    );

    if (!lazyLoad) {
      injectExternalVideo(external, container);
    } else {
      observer.observe(container);
    }

    container.dataset.externalReady = 'true';
  }

  function initAll(scope) {
    const root = scope || document;
    const containers = root.querySelectorAll(SELECTORS.container);

    containers.forEach(function (container) {
      initNativeVideo(container);
      initExternalVideo(container);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:reorder', function (event) {
    initAll(event.target);
  });
})();